/**
 * TaskTimeInput — half-hour steppers for AM / Designer / Editor and an
 * iteration counter. Visible only to Team-side roles (admin, team, AM).
 * Auto-saves via PUT /engagements/{eid}/{kind}s/{id}/time.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Minus, Plus, Repeat, Clock } from "lucide-react";

const HALF = 0.5;

function fmtHrs(v) {
  const n = Number(v || 0);
  if (n === 0) return "0";
  return n.toFixed(1).replace(/\.0$/, "");
}

function Stepper({ label, value, onChange, testid, step = HALF }) {
  return (
    <div className="flex items-center gap-1 select-none">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono w-14">{label}</span>
      <button type="button"
        onClick={() => onChange(Math.max(0, +(value || 0) - step))}
        className="w-5 h-5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
        data-testid={`${testid}-minus`}>
        <Minus className="w-2.5 h-2.5" />
      </button>
      <span className="w-8 text-center text-[11px] font-mono text-zinc-200 tabular-nums" data-testid={`${testid}-value`}>
        {fmtHrs(value)}h
      </span>
      <button type="button"
        onClick={() => onChange(+(value || 0) + step)}
        className="w-5 h-5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
        data-testid={`${testid}-plus`}>
        <Plus className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}

export default function TaskTimeInput({
  engagementId, kind /* "deliverable"|"meeting" */, itemId,
  initial, onSaved, compact = false,
}) {
  const [am, setAm] = useState(initial?.hours_am ?? 0);
  const [de, setDe] = useState(initial?.hours_designer ?? 0);
  const [ed, setEd] = useState(initial?.hours_editor ?? 0);
  const [it, setIt] = useState(initial?.iterations ?? 0);

  useEffect(() => {
    setAm(initial?.hours_am ?? 0);
    setDe(initial?.hours_designer ?? 0);
    setEd(initial?.hours_editor ?? 0);
    setIt(initial?.iterations ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const save = async (patch) => {
    try {
      const path = kind === "meeting" ? "meetings" : "deliverables";
      await api.put(`/engagements/${engagementId}/${path}/${itemId}/time`, patch);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const setAmS = (v) => { setAm(v); save({ hours_am: v }); };
  const setDeS = (v) => { setDe(v); save({ hours_designer: v }); };
  const setEdS = (v) => { setEd(v); save({ hours_editor: v }); };
  const setItS = (v) => { setIt(v); save({ iterations: v }); };

  const total = (Number(am) || 0) + (Number(de) || 0) + (Number(ed) || 0);

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${compact ? "" : "mt-1.5"}`}
      data-testid={`time-input-${itemId}`}>
      <Stepper label="AM"       value={am} onChange={setAmS} testid={`time-am-${itemId}`} />
      <Stepper label="Designer" value={de} onChange={setDeS} testid={`time-de-${itemId}`} />
      <Stepper label="Editor"   value={ed} onChange={setEdS} testid={`time-ed-${itemId}`} />
      <div className="flex items-center gap-1">
        <Repeat className="w-3 h-3 text-zinc-500" />
        <button type="button" onClick={() => setItS(Math.max(0, (Number(it) || 0) - 1))}
          className="w-5 h-5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
          data-testid={`iter-minus-${itemId}`}>
          <Minus className="w-2.5 h-2.5" />
        </button>
        <span className="w-6 text-center text-[11px] font-mono text-zinc-200 tabular-nums"
          data-testid={`iter-value-${itemId}`}>
          v{it || 0}
        </span>
        <button type="button" onClick={() => setItS((Number(it) || 0) + 1)}
          className="w-5 h-5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
          data-testid={`iter-plus-${itemId}`}>
          <Plus className="w-2.5 h-2.5" />
        </button>
      </div>
      <span className="text-[10px] text-zinc-600 font-mono flex items-center gap-1">
        <Clock className="w-2.5 h-2.5" /> {fmtHrs(total)}h total
      </span>
    </div>
  );
}
